/**
 * HR Module - Data Tab Components (All 12 tabs)
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, Paperclip, MapPin } from 'lucide-react';
import { Section, DataTable, Badge, SkillBadge, MoodBadge } from './UIComponents';
import { RATING_COLORS, getLevelDiff } from './constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// KPI Tab
export const KPITab = ({ data, onAdd, onEdit, onDelete }) => (
  <Section title="KPI & Sasaran" onAdd={onAdd}>
    <DataTable 
      columns={[
        { label: 'Staf', key: 'staff_name' },
        { label: 'Tempoh', key: 'period' },
        { label: 'KPI', key: 'kpi_name' },
        { label: 'Sasaran', render: r => `${r.target_value} ${r.unit}` },
        { label: 'Sebenar', render: r => `${r.actual_value} ${r.unit}` },
        { label: '%', render: r => (
          <span className={r.actual_value >= r.target_value ? 'text-emerald-600 font-bold' : 'text-red-600'}>
            {Math.round((r.actual_value / r.target_value) * 100)}%
          </span>
        )},
        { label: 'Status', render: r => <Badge status={r.status} /> }
      ]} 
      data={data || []} 
      onEdit={onEdit} 
      onDelete={onDelete} 
    />
  </Section>
);

// Performance Tab
export const PerformanceTab = ({ data, onAdd, onEdit, onDelete }) => (
  <Section title="Penilaian Prestasi" onAdd={onAdd}>
    <DataTable 
      columns={[
        { label: 'Staf', key: 'staff_name' },
        { label: 'Tempoh', key: 'period' },
        { label: 'Skor', render: r => (
          <span className="font-bold" style={{ color: r.overall_score >= 80 ? '#10B981' : r.overall_score >= 60 ? '#F59E0B' : '#EF4444' }}>
            {r.overall_score}%
          </span>
        ), align: 'text-center' },
        { label: 'Penilaian', render: r => (
          <span className="px-2 py-1 rounded-full text-xs text-white" style={{ background: RATING_COLORS[r.rating] }}>
            {r.rating}
          </span>
        )},
        { label: 'Flag', render: r => (
          <div className="flex gap-1">
            {r.needs_training && <span className="px-1 bg-amber-100 text-amber-700 text-xs rounded">Latihan</span>}
            {r.needs_consultation && <span className="px-1 bg-blue-100 text-blue-700 text-xs rounded">Konsult</span>}
          </div>
        )}
      ]} 
      data={data || []} 
      onEdit={onEdit} 
      onDelete={onDelete} 
    />
  </Section>
);

// Attendance Tab — now with GPS drawer for each row (self-service clock-ins)
export const AttendanceTab = ({ data, onAdd, onEdit, onDelete, onImport, importing }) => {
  const [gpsOpen, setGpsOpen] = useState(false);
  const [gpsData, setGpsData] = useState(null); // { record, office }
  const [gpsLoading, setGpsLoading] = useState(false);

  const openGpsDrawer = async (row) => {
    setGpsOpen(true); setGpsLoading(true); setGpsData(null);
    try {
      const { data: resp } = await axios.get(`${API}/hr/clock-records`, {
        params: { date: row.date, staff_id: row.staff_id },
      });
      if (resp.records?.length) {
        setGpsData({ record: resp.records[0], office: resp.office });
      } else {
        setGpsData({ record: null, office: resp.office, message: 'Tiada rekod GPS self-service untuk hari ini (kemungkinan punch PDF / admin entry).' });
      }
    } catch (e) {
      toast.error('Gagal load data GPS');
      setGpsData({ record: null });
    } finally { setGpsLoading(false); }
  };

  return (
    <>
      <Section
        title="Kehadiran"
        onAdd={onAdd}
        extraBtn={
          <label className={`px-3 py-2 border border-emerald-600 text-emerald-700 rounded-lg flex items-center gap-2 text-sm cursor-pointer hover:bg-emerald-50 ${importing ? 'opacity-50' : ''}`} data-testid="attendance-import-label">
            <Upload className="w-4 h-4" /> Import
            <input type="file" accept=".xlsx,.csv,.pdf" onChange={onImport} className="hidden" disabled={importing} data-testid="attendance-import-input" />
          </label>
        }
      >
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-4 text-xs text-blue-700">
          <FileSpreadsheet className="w-4 h-4 inline mr-1" /> CSV/Excel: staff_name, date (YYYY-MM-DD), status, check_in, check_out, notes &nbsp;•&nbsp; PDF: Timesheet Report (auto-parse + analytics, lewat &gt; 9:30 AM)
        </div>
        <DataTable
          columns={[
            { label: 'Tarikh', key: 'date' },
            { label: 'Staf', key: 'staff_name' },
            { label: 'Masuk', key: 'check_in', align: 'text-center' },
            { label: 'Keluar', key: 'check_out', align: 'text-center' },
            { label: 'Status', render: r => <Badge status={r.status} /> },
            { label: 'GPS / Selfie', align: 'text-center', render: r => (
              <button
                data-testid={`view-gps-${r.id || r.date + r.staff_name}`}
                onClick={() => openGpsDrawer(r)}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200"
                title="Lihat GPS + selfie check-in"
              >
                <MapPin className="w-3 h-3" /> View
              </button>
            ) },
          ]}
          data={(data || []).slice(0, 50)}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </Section>

      {gpsOpen && (
        <AttendanceGpsDrawer
          loading={gpsLoading}
          payload={gpsData}
          onClose={() => { setGpsOpen(false); setGpsData(null); }}
        />
      )}
    </>
  );
};

// Haversine distance between two coords (meters)
const _haversine = (lat1, lng1, lat2, lng2) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1); const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const AttendanceGpsDrawer = ({ loading, payload, onClose }) => {
  const r = payload?.record;
  const office = payload?.office;
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('ms-MY', { hour: '2-digit', minute: '2-digit' }) : '—';
  // OpenStreetMap static tile URL (embed iframe) — free & key-less
  const mapSrc = r && r.clock_in_lat ? (
    `https://www.openstreetmap.org/export/embed.html?bbox=${r.clock_in_lng - 0.004},${r.clock_in_lat - 0.004},${r.clock_in_lng + 0.004},${r.clock_in_lat + 0.004}&layer=mapnik&marker=${r.clock_in_lat},${r.clock_in_lng}`
  ) : null;
  const inDist = r && office ? _haversine(r.clock_in_lat, r.clock_in_lng, office.lat, office.lng) : null;
  const outDist = r && office && r.clock_out_lat ? _haversine(r.clock_out_lat, r.clock_out_lng, office.lat, office.lng) : null;

  return (
    <div data-testid="gps-drawer" className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-xl">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-indigo-600" />
            Lokasi & Selfie Clock-In
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Memuat data GPS...</div>
          ) : !r ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
              {payload?.message || 'Rekod tiada data GPS self-service untuk hari ini.'}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[10px] uppercase text-gray-400 tracking-wider font-semibold">Staf</p>
                  <p className="font-semibold text-gray-800 truncate">{r.staff_name}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-gray-400 tracking-wider font-semibold">Tarikh</p>
                  <p className="font-semibold text-gray-800">{r.date}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-gray-400 tracking-wider font-semibold">Masa Masuk</p>
                  <p className="font-bold text-emerald-700">{fmtTime(r.clock_in_time)}</p>
                  {inDist != null && <p className="text-[11px] text-gray-500">Jarak: {inDist}m {inDist <= (office?.radius_meters || 150) ? '✓ dalam radius' : '⚠ luar radius'}</p>}
                </div>
                <div>
                  <p className="text-[10px] uppercase text-gray-400 tracking-wider font-semibold">Masa Keluar</p>
                  <p className="font-bold text-blue-700">{fmtTime(r.clock_out_time)}</p>
                  {outDist != null && <p className="text-[11px] text-gray-500">Jarak: {outDist}m</p>}
                  {r.auto_clock_out && <p className="text-[11px] text-amber-600">Auto clock-out (5 PM)</p>}
                </div>
              </div>

              {mapSrc && (
                <div className="rounded-xl overflow-hidden border border-gray-200">
                  <iframe
                    data-testid="gps-map-iframe"
                    title="GPS Clock-In Location"
                    src={mapSrc}
                    width="100%"
                    height="280"
                    style={{ border: 0 }}
                  />
                  <div className="text-[10px] text-gray-500 px-2 py-1 bg-gray-50">
                    Pin = lokasi clock-in staf · OpenStreetMap
                  </div>
                </div>
              )}

              {r.clock_in_selfie_b64 ? (
                <div>
                  <p className="text-[10px] uppercase text-gray-400 tracking-wider font-semibold mb-1.5">Selfie Clock-In</p>
                  <img src={r.clock_in_selfie_b64} alt="Selfie"
                    className="w-32 h-32 rounded-xl object-cover border border-gray-200" />
                </div>
              ) : (
                <div className="text-xs text-gray-400 italic">Tiada selfie diambil untuk sesi ini.</div>
              )}

              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-gray-500">GPS Accuracy</p>
                  <p className="font-semibold">{r.clock_in_accuracy ? `${Math.round(r.clock_in_accuracy)}m` : '—'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-gray-500">Work Hours</p>
                  <p className="font-semibold">{r.work_hours ?? '—'} jam</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Leave Tab — with Approve/Reject inline actions + mandatory rejection reason modal
export const LeaveTab = ({ data, onAdd, onEdit, onDelete, onChange }) => {
  const [rejectTarget, setRejectTarget] = useState(null); // leave row being rejected
  const [rejectReason, setRejectReason] = useState('');
  const [busyId, setBusyId] = useState(null);

  const handleApprove = async (row, level = 'L2') => {
    if (busyId) return;
    setBusyId(row.id);
    try {
      await axios.post(`${API}/hr/leave/${row.id}/approve`, { level });
      toast.success(`Cuti diluluskan: ${row.staff_name}`);
      if (onChange) await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal luluskan cuti');
    } finally { setBusyId(null); }
  };

  const confirmReject = async () => {
    if (!rejectReason.trim()) { toast.error('Alasan penolakan wajib diisi.'); return; }
    setBusyId(rejectTarget.id);
    try {
      await axios.post(`${API}/hr/leave/${rejectTarget.id}/reject`, { reason: rejectReason.trim() });
      toast.success(`Cuti ditolak: ${rejectTarget.staff_name}`);
      setRejectTarget(null); setRejectReason('');
      if (onChange) await onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal tolak cuti');
    } finally { setBusyId(null); }
  };

  // Sort so pending (waiting on me) appear first
  const sorted = [...(data || [])].sort((a, b) => {
    const rank = { pending: 0, pending_l2: 1, approved: 2, rejected: 3 };
    return (rank[a.status] ?? 4) - (rank[b.status] ?? 4);
  });

  return (
    <>
      <Section title="Pengurusan Cuti" onAdd={onAdd}>
        <DataTable
          columns={[
            { label: 'Staf', key: 'staff_name' },
            { label: 'Jenis', key: 'leave_type' },
            { label: 'Dari', key: 'start_date' },
            { label: 'Hingga', key: 'end_date' },
            { label: 'Hari', key: 'days', align: 'text-center' },
            { label: 'Lampiran', align: 'text-center', render: r => r.attachment_url ?
              <a href={r.attachment_url} target="_blank" rel="noreferrer" title="Buka lampiran" className="inline-flex text-indigo-600 hover:text-indigo-800"><Paperclip className="w-4 h-4" /></a>
              : <span className="text-gray-300">—</span>
            },
            { label: 'Status', render: r => <Badge status={r.status} /> },
            { label: 'Tindakan', align: 'text-center', render: r => {
              const pending = r.status === 'pending' || r.status === 'pending_l2';
              if (!pending) return <span className="text-gray-300 text-xs">—</span>;
              const disabled = busyId === r.id;
              return (
                <div className="flex items-center gap-1.5 justify-center">
                  <button
                    data-testid={`approve-leave-${r.id}`}
                    onClick={() => handleApprove(r)}
                    disabled={disabled}
                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1"
                  >
                    <CheckCircle className="w-3 h-3" /> Lulus
                  </button>
                  <button
                    data-testid={`reject-leave-${r.id}`}
                    onClick={() => { setRejectTarget(r); setRejectReason(''); }}
                    disabled={disabled}
                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1"
                  >
                    <XCircle className="w-3 h-3" /> Tolak
                  </button>
                </div>
              );
            } },
          ]}
          data={sorted}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </Section>

      {rejectTarget && (
        <div data-testid="reject-leave-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => { setRejectTarget(null); setRejectReason(''); }}>
          <div onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl w-full max-w-md p-5 shadow-xl space-y-3">
            <h3 className="font-bold text-gray-800">Tolak Permohonan Cuti</h3>
            <p className="text-xs text-gray-600">
              Staf: <strong>{rejectTarget.staff_name}</strong> · {rejectTarget.leave_type} · {rejectTarget.start_date} → {rejectTarget.end_date} ({rejectTarget.days} hari)
            </p>
            <label className="block">
              <span className="text-xs text-red-600 font-semibold">Alasan Penolakan (Wajib) *</span>
              <textarea
                data-testid="reject-reason-input"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                autoFocus
                placeholder="Contoh: Kurang staf on duty pada tarikh tersebut. Sila pilih tarikh lain."
                className="w-full mt-1 px-3 py-2 border border-red-200 rounded-lg text-sm focus:ring-2 focus:ring-red-300 outline-none"
              />
            </label>
            <div className="flex gap-2 pt-1">
              <button onClick={() => { setRejectTarget(null); setRejectReason(''); }}
                className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-50">
                Batal
              </button>
              <button
                data-testid="confirm-reject-btn"
                onClick={confirmReject}
                disabled={busyId === rejectTarget.id || !rejectReason.trim()}
                className="flex-1 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                {busyId === rejectTarget.id ? 'Menolak...' : 'Sahkan Tolak'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Training Tab
export const TrainingTab = ({ data, onAdd, onEdit, onDelete }) => (
  <Section title="Latihan" onAdd={onAdd}>
    <DataTable 
      columns={[
        { label: 'Staf', key: 'staff_name' },
        { label: 'Latihan', key: 'training_name' },
        { label: 'Jenis', key: 'training_type' },
        { label: 'Tarikh', key: 'start_date' },
        { label: 'Status', render: r => <Badge status={r.status} /> },
        { label: 'Sijil', render: r => r.certificate_obtained ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" /> : '-', align: 'text-center' }
      ]} 
      data={data || []} 
      onEdit={onEdit} 
      onDelete={onDelete} 
    />
  </Section>
);

// Disciplinary Tab
export const DisciplinaryTab = ({ data, onAdd, onEdit, onDelete }) => (
  <Section title="Tindakan Disiplin" onAdd={onAdd}>
    <DataTable 
      columns={[
        { label: 'Tarikh', key: 'date' },
        { label: 'Staf', key: 'staff_name' },
        { label: 'Jenis', render: r => (
          <span className={`px-2 py-1 rounded text-xs ${r.type?.includes('Akhir') ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
            {r.type}
          </span>
        )},
        { label: 'Sebab', key: 'reason' },
        { label: 'Tamat', key: 'expiry_date' }
      ]} 
      data={data || []} 
      onEdit={onEdit} 
      onDelete={onDelete} 
    />
  </Section>
);

// Skill Tab
export const SkillTab = ({ data, onAdd, onEdit, onDelete }) => (
  <Section title="Skill Matrix" onAdd={onAdd}>
    <DataTable 
      columns={[
        { label: 'Staf', key: 'staff_name' },
        { label: 'Kemahiran', key: 'skill_name' },
        { label: 'Kategori', key: 'category' },
        { label: 'Semasa', render: r => <SkillBadge level={r.current_level} /> },
        { label: 'Diperlukan', render: r => <SkillBadge level={r.required_level} /> },
        { label: 'Gap', render: r => {
          const gap = getLevelDiff(r.required_level, r.current_level);
          return gap > 0 ? <span className="text-red-600 font-bold">-{gap}</span> : <span className="text-emerald-600">✓</span>;
        }}
      ]} 
      data={data || []} 
      onEdit={onEdit} 
      onDelete={onDelete} 
    />
  </Section>
);

// Contract Tab
export const ContractTab = ({ data, onAdd, onEdit, onDelete }) => (
  <Section title="Kontrak & Probation" onAdd={onAdd}>
    <DataTable 
      columns={[
        { label: 'Staf', key: 'staff_name' },
        { label: 'Jenis', key: 'contract_type' },
        { label: 'Mula', key: 'start_date' },
        { label: 'Tamat', key: 'end_date' },
        { label: 'Probation', key: 'probation_end' },
        { label: 'Status', render: r => <Badge status={r.probation_status} /> }
      ]} 
      data={data || []} 
      onEdit={onEdit} 
      onDelete={onDelete} 
    />
  </Section>
);

// Meeting Tab
export const MeetingTab = ({ data, onAdd, onEdit, onDelete }) => (
  <Section title="1-on-1 Meeting" onAdd={onAdd}>
    <DataTable 
      columns={[
        { label: 'Tarikh', key: 'date' },
        { label: 'Staf', key: 'staff_name' },
        { label: 'Pengurus', key: 'conducted_by' },
        { label: 'Mood', render: r => <MoodBadge mood={r.staff_mood} /> },
        { label: 'Susulan', key: 'follow_up_date' }
      ]} 
      data={data || []} 
      onEdit={onEdit} 
      onDelete={onDelete} 
    />
  </Section>
);

// Reward Tab
export const RewardTab = ({ data, onAdd, onEdit, onDelete }) => (
  <Section title="Ganjaran & Pengiktirafan" onAdd={onAdd}>
    <DataTable 
      columns={[
        { label: 'Tarikh', key: 'date' },
        { label: 'Staf', key: 'staff_name' },
        { label: 'Jenis', key: 'reward_type' },
        { label: 'Tajuk', key: 'title' },
        { label: 'Nilai', render: r => r.value ? `RM${r.value.toLocaleString()}` : '-' }
      ]} 
      data={data || []} 
      onEdit={onEdit} 
      onDelete={onDelete} 
    />
  </Section>
);

// Exit Tab
export const ExitTab = ({ data, onAdd, onDelete }) => (
  <Section title="Exit Interview & Turnover" onAdd={onAdd}>
    <DataTable 
      columns={[
        { label: 'Staf', key: 'staff_name' },
        { label: 'Tarikh Akhir', key: 'last_working_date' },
        { label: 'Sebab', key: 'exit_reason' },
        { label: 'Kepuasan', render: r => (
          <span className={r.satisfaction_score >= 4 ? 'text-emerald-600' : r.satisfaction_score >= 3 ? 'text-amber-600' : 'text-red-600'}>
            {r.satisfaction_score}/5
          </span>
        )},
        { label: 'Cadang?', render: r => r.would_recommend ? '✓' : '✗' }
      ]} 
      data={data || []} 
      onDelete={onDelete} 
    />
  </Section>
);
